// Report periods in Paris wall-clock time, shared by the dumpers, the cron and the front (plan 2026-10-20).
// Rule: end = next slot, start = min(end − 7 days, previous end), number = ISO week of end − 1 ms.

const TIME_ZONE = 'Europe/Paris';
const WEEKDAYS = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
const partsFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE, hourCycle: 'h23', weekday: 'short',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
});

export const parisParts = (instant) => {
  const parts = Object.fromEntries(partsFormat.formatToParts(new Date(instant)).map(({ type, value }) => [type, value]));
  return {
    year: +parts.year, month: +parts.month, day: +parts.day,
    hour: +parts.hour, minute: +parts.minute, second: +parts.second, weekday: WEEKDAYS[parts.weekday]
  };
};

const offsetMs = (time) => {
  const p = parisParts(time);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(time / 1000) * 1000;
};

/** Instant of a Paris wall-clock time; day overflow is allowed (day 32 = next month). */
export const fromParis = (year, month, day, hour = 0, minute = 0) => {
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const first = wall - offsetMs(wall);
  return new Date(wall - offsetMs(first));
};

/** Same Paris wall-clock time n calendar days later (a DST day is 23 h or 25 h long). */
export const addParisDays = (instant, days) => {
  const p = parisParts(instant);
  return fromParis(p.year, p.month, p.day + days, p.hour, p.minute);
};

/** First occurrence of (ISO day, hour) strictly after the instant. */
export const nextSlot = (after, day, hour) => {
  const p = parisParts(after);
  const offset = (day - p.weekday + 7) % 7;
  const candidate = fromParis(p.year, p.month, p.day + offset, hour);
  return candidate.getTime() > new Date(after).getTime() ? candidate : fromParis(p.year, p.month, p.day + offset + 7, hour);
};

export const isoWeekOf = (instant) => {
  const p = parisParts(instant);
  const thursday = new Date(Date.UTC(p.year, p.month - 1, p.day + 4 - p.weekday));
  const year = thursday.getUTCFullYear();
  const week = Math.ceil(((thursday - Date.UTC(year, 0, 1)) / 86400000 + 1) / 7);
  return { year, week };
};

export const reportWeek = (end) => isoWeekOf(new Date(end).getTime() - 1);

const weekKey = ({ year, week }) => year * 100 + week;

/** `last` = latest weekly or null; `scheduleUpdatedAt` stops a slot change from back-firing a past report. */
export const nextWeeklyPeriod = ({ last = null, day = 1, hour = 0, scheduleUpdatedAt = null, now = new Date() }) => {
  const weekAgo = new Date(now).getTime() - 7 * 86400000;
  const lastEndRaw = last?.period_end ? new Date(last.period_end).getTime() : null;
  // A latest report older than 14 days (reactivated client, long outage) restarts on the last 7 days instead of back-filling every week.
  const lastEnd = lastEndRaw !== null && lastEndRaw >= weekAgo - 7 * 86400000 ? lastEndRaw : null;
  const floor = lastEnd ?? weekAgo;
  let end = nextSlot(Math.max(floor, scheduleUpdatedAt ? new Date(scheduleUpdatedAt).getTime() : 0), day, hour);

  if (last)
    while (weekKey(reportWeek(end)) <= weekKey({ year: +last.year, week: +last.week_number }))
      end = nextSlot(end, day, hour);

  const sevenDaysBefore = addParisDays(end, -7);
  const start = lastEnd !== null && lastEnd < sevenDaysBefore.getTime() ? new Date(lastEnd) : sevenDaysBefore;
  return { start, end, ...reportWeek(end) };
};

export const periodOfIsoWeek = (year, week, day = 1, hour = 0) => {
  const jan4 = new Date(Date.UTC(+year, 0, 4));
  const mondayDay = 4 - ((jan4.getUTCDay() + 6) % 7) + (+week - 1) * 7;
  const end = nextSlot(fromParis(+year, 1, mondayDay, 0), day, hour);
  return { start: addParisDays(end, -7), end, ...reportWeek(end) };
};

/** Yesterday, 0 h → 0 h Paris, numbered by its own ISO week. */
export const dailyPeriod = (now = new Date()) => {
  const p = parisParts(now);
  const end = fromParis(p.year, p.month, p.day, 0);
  const start = addParisDays(end, -1);
  return { start, end, ...isoWeekOf(start) };
};

/** Not complete yet, for the cron and the UI alike: no dump up to the end of its period. */
export const isSnapshot = (report) => !!report?.period_end && !(new Date(report.dumped_at ?? NaN) >= new Date(report.period_end));

const DAY_MS = 86400000;
const time = (value) => (value ? new Date(value).getTime() : NaN);
const round1 = (value) => Math.round(value * 10) / 10;

/** Saved weekly slot of a client config; Monday 0 h when unset. */
export const slotOf = (config) => ({ day: config?.report_day || 1, hour: config?.report_hour || 0 });

/** Period of a report row; legacy rows without one use their ISO week, Monday 0 h → Monday 0 h. */
export const reportPeriodOf = (report) => (report?.period_start
  ? { start: report.period_start, end: report.period_end }
  : periodOfIsoWeek(report?.year, report?.week_number));

/** Length of a period in days, one decimal (a DST week still counts 7). */
export const periodDays = (start, end) => round1((time(end) - time(start)) / DAY_MS);

/** Days of [start, end] already covered by a previous period ending at previousEnd, one decimal. */
export const overlapDays = (previousEnd, start, end) => {
  const overlap = Math.min(time(previousEnd), time(end)) - time(start);
  return overlap > 0 ? round1(overlap / DAY_MS) : 0;
};

export const sameParisDay = (a, b) => {
  const pa = parisParts(a);
  const pb = parisParts(b);
  return pa.year === pb.year && pa.month === pb.month && pa.day === pb.day;
};

/** What « Générer maintenant » produces: the latest report while its period runs, else the next period. */
export const generateNowPeriod = ({ latest = null, day, hour, scheduleUpdatedAt = null, now = new Date() }) => {
  if (latest?.period_end && time(latest.period_end) > new Date(now).getTime())
    return { start: new Date(latest.period_start), end: new Date(latest.period_end), year: +latest.year, week: +latest.week_number, ongoing: true };
  const last = latest && { ...latest, period_end: reportPeriodOf(latest).end };
  return { ...nextWeeklyPeriod({ last, day, hour, scheduleUpdatedAt, now }), ongoing: false };
};
